import { FormContainer, notify } from '@wyz/ui';
import clsx from 'clsx';
import { memo } from 'react';
import { useForm } from 'react-hook-form';
import { Trans } from '~/common/context';
import { PurchaseErrorCodes } from '~/common/helpers';
import { usePurchaseErrorTranslation } from '~/common/hooks';
import { IUserCustomer } from '~/common/types';
import { useManageWcwCart } from '~/purchase/api';
import { PurchaseTable, WcwOfferRow } from '~/purchase/components';
import { WcwCartHelpers } from '~/purchase/helpers';
import { IWcwOffer } from '~/purchase/types';

type Props = {
  product: IWcwOffer;
  customer: IUserCustomer;
  viewPrice: boolean;
};

function WcwTyreGroupRow({ product, customer, viewPrice }: Props) {
  const wcwCart = useManageWcwCart({
    context: { customerId: customer.id },
    add: { enabled: true },
    get: { enabled: true },
    remove: { enabled: true },
  });
  const methods = useForm({
    defaultValues: {
      quantity: product.offers.map(() => product.wheel.salesUnit),
    },
  });
  const { resolveErrorKey } = usePurchaseErrorTranslation();

  const cartItemIds = (wcwCart.get.data?.data.items ?? [])
    .filter(
      (item) =>
        item.wheel.reference === product.wheel.reference &&
        item.tyre.ean === product.tyre.ean,
    )
    .map((item) => item.id);

  const handleRemove = () => {
    cartItemIds.forEach((id) => {
      wcwCart.remove.mutate(id, {
        onError: async (error) => {
          if (error.message === PurchaseErrorCodes.ERR_INVALID_CART) {
            await wcwCart.get.refetch();
          }
        },
      });
    });
  };

  return (
    <FormContainer
      methods={methods}
      id={product.id}
      submitHandler={() => {
        wcwCart.add.mutate(WcwCartHelpers.prepareAddWcwCartPayload(product), {
          onSuccess: () => {
            notify.success(
              <Trans i18nKey='app.purchase.cart.success.order' />,
              { id: 'toast-success-wcw-cart-add-to-cart' },
            );
          },
          onError: (error) => {
            notify.error(
              <Trans
                i18nKey={resolveErrorKey(error.message)}
                values={error?.meta}
              />,
            );
          },
        });
      }}
    >
      <PurchaseTable
        className={clsx('wcw-table', !viewPrice && 'wcw-table--hide-price')}
      >
        <tbody>
          {product.offers.map((offer, offerIndex) => (
            <WcwOfferRow
              key={`${product.id}-${offer.supplier.id}-${offerIndex}`}
              product={product}
              offer={offer}
              offerIndex={offerIndex}
              offersCount={product.offers.length}
              showCommon={offerIndex === 0}
              viewPrice={viewPrice}
              isAdding={wcwCart.add.isLoading}
              isInCart={cartItemIds.length > 0}
              isRemoving={wcwCart.remove.isLoading}
              onRemove={handleRemove}
            />
          ))}
        </tbody>
      </PurchaseTable>
    </FormContainer>
  );
}

export default memo(WcwTyreGroupRow);
